import axios from 'axios';
import { API_CONFIG } from '../config/api';

const adminRoot = `${API_CONFIG.BASE_URL.replace(/\/api\/?$/, '')}/apiadmin`;
let refreshPromise = null;

const messageFromError = (error) => {
  const data = error?.response?.data;
  if (typeof data === 'string') return data;
  if (typeof data?.detail === 'string') return data.detail;
  if (typeof data?.error === 'string') return data.error;
  if (data && typeof data === 'object') {
    return Object.entries(data)
      .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : String(value)}`)
      .join(' · ');
  }
  return error?.message || 'The request could not be completed.';
};

const clearSession = () => {
  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_refresh_token');
  localStorage.removeItem('admin_user');
};

const refreshAccessToken = () => {
  const refresh = localStorage.getItem('admin_refresh_token');
  if (!refresh) return Promise.reject(new Error('Admin session expired.'));
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${adminRoot}/refresh/`, { refresh }, { timeout: 30000 })
      .then(({ data }) => {
        localStorage.setItem('admin_token', data.access);
        if (data.refresh) localStorage.setItem('admin_refresh_token', data.refresh);
        return data.access;
      })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
};

export const client = axios.create({
  baseURL: adminRoot,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const request = error.config;
    const refresh = localStorage.getItem('admin_refresh_token');
    const isAuthRequest = request?.url?.includes('/token/') || request?.url?.includes('/refresh/');

    if (error.response?.status === 401 && refresh && request && !request._adminRetry && !isAuthRequest) {
      request._adminRetry = true;
      try {
        const token = await refreshAccessToken();
        request.headers.Authorization = `Bearer ${token}`;
        return client(request);
      } catch (refreshError) {
        clearSession();
        window.dispatchEvent(new Event('admin-session-expired'));
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  },
);

export const unwrap = async (request) => {
  try {
    const response = await request;
    return response.data;
  } catch (error) {
    const wrapped = new Error(messageFromError(error));
    wrapped.status = error?.response?.status;
    wrapped.details = error?.response?.data;
    throw wrapped;
  }
};

const adminOperationsService = {
  async login(credentials) {
    const data = await unwrap(axios.post(`${adminRoot}/token/`, credentials, { timeout: 30000 }));
    localStorage.setItem('admin_token', data.access);
    localStorage.setItem('admin_refresh_token', data.refresh);
    localStorage.setItem('admin_user', JSON.stringify(data.user || {}));
    return data.user || {};
  },

  async verifySession() {
    let token = localStorage.getItem('admin_token');
    if (!token && !localStorage.getItem('admin_refresh_token')) return null;
    try {
      if (!token) token = await refreshAccessToken();
      let data;
      try {
        data = await unwrap(axios.post(`${adminRoot}/verify/`, { token }, { timeout: 15000 }));
      } catch (verifyError) {
        if (verifyError.status !== 401 || !localStorage.getItem('admin_refresh_token')) throw verifyError;
        token = await refreshAccessToken();
        data = await unwrap(axios.post(`${adminRoot}/verify/`, { token }, { timeout: 15000 }));
      }
      if (data.user) localStorage.setItem('admin_user', JSON.stringify(data.user));
      return data.user || JSON.parse(localStorage.getItem('admin_user') || 'null');
    } catch (error) {
      clearSession();
      return null;
    }
  },

  logout: clearSession,
  getStoredUser: () => { try { return JSON.parse(localStorage.getItem('admin_user') || 'null'); } catch { return null; } },
  getOverview: () => unwrap(client.get('/operations/overview/')),
  getAssets: () => unwrap(client.get('/operations/assets/')),
  updateAsset: (symbol, changes) => unwrap(client.patch(`/operations/assets/${encodeURIComponent(symbol)}/`, changes)),
  createPair: (data) => unwrap(client.post('/operations/pairs/', data)),
  updatePair: (id, changes) => unwrap(client.patch(`/operations/pairs/${id}/`, changes)),
  getWalletLedger: (params) => unwrap(client.get('/operations/wallet-ledger/', { params })),
  getWalletBalances: (params) => unwrap(client.get('/operations/wallet-balances/', { params })),
  getNotifications: () => unwrap(client.get('/operations/notifications/')),
  createNotification: (data) => unwrap(client.post('/operations/notifications/', data)),
  updateNotification: (id, data) => unwrap(client.patch(`/operations/notifications/${id}/`, data)),
  deleteNotification: (id) => unwrap(client.delete(`/operations/notifications/${id}/`)),
  getSystemHealth: () => unwrap(client.get('/operations/system-health/')),
  getCustomerWithdrawalAllowance: (user) => unwrap(client.get('/operations/customer-withdrawal-allowance/', { params: { user } })),
  getTreasuryRisk: (refresh = false) => unwrap(client.get('/operations/treasury-risk/', { params: refresh ? { refresh: 1 } : {} })),
  getOperationJobs: (params = {}) => unwrap(client.get('/operations/jobs/', { params })),
  retryOperationJob: (id, data) => unwrap(client.post(`/operations/jobs/${id}/retry/`, data)),
  messageFromError,
};

export default adminOperationsService;
